import { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Tabs,
  Tab,
  Alert,
  Card,
  Button,
  Form,
  Spinner,
  Table,
  Badge,
} from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  billingService,
  BillingInvoice,
  BillingDocument,
  AdminSubscriptionRosterRow,
} from '../services/billing.service';
import { Link } from 'react-router-dom';
import SubscriptionCard from '../components/billing/SubscriptionCard';
import InvoiceTable from '../components/billing/InvoiceTable';
import InvoiceDetailModal from '../components/billing/InvoiceDetailModal';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import api from '../services/api';
import { isBillingEnabled } from '../utils/config';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface DashboardStats {
  mrr_usd: number;
  current_month_revenue_usd: number;
  last_month_revenue_usd: number;
  last_year_month_revenue_usd: number;
  yoy_growth_percent: number;
  active_users: number;
  arpu_usd: number;
  new_subscriptions: number;
  cancelled_subscriptions: number;
  monthly_revenue: Array<{ month: string; revenue_usd: number }>;
}

interface BillingSettings {
  INV_COMPANY_NAME?: string;
  INV_COMPANY_ADDRESS?: string;
  INV_REG_NUMBER?: string;
  MAIL_FROM?: string;
  MAIL_ADMIN?: string;
  [key: string]: string | undefined;
}

function billingApiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return message || fallback;
}

function overrideLabel(row: AdminSubscriptionRosterRow): string {
  const override = row.override;
  if (!override) return '—';
  if (override.suspended) return '停止';
  if (override.permanentlyFree) return '恒久無料';
  if (override.freeUntil && new Date(override.freeUntil) > new Date()) {
    return `無料期限 ${override.freeUntil.slice(0, 10)}`;
  }
  return '—';
}

function AdminSubscriptionPanel() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [priceId, setPriceId] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const configQuery = useQuery({
    queryKey: ['billing', 'admin-subscription-config'],
    queryFn: () => billingService.getAdminSubscriptionConfig(),
  });
  const rosterQuery = useQuery({
    queryKey: ['billing', 'admin-subscriptions'],
    queryFn: () => billingService.listAdminSubscriptions(),
  });

  useEffect(() => {
    if (configQuery.data?.defaultPriceId && !priceId) {
      setPriceId(configQuery.data.defaultPriceId);
    }
  }, [configQuery.data?.defaultPriceId, priceId]);

  const createMutation = useMutation({
    mutationFn: () =>
      billingService.createAdminSubscription({
        userId: Number(userId),
        priceId: priceId.trim() || undefined,
      }),
    onSuccess: (data) => {
      setError('');
      setNotice(
        data.hostedInvoiceUrl
          ? `請求を作成しました。Stripe の請求URL: ${data.hostedInvoiceUrl}`
          : `請求を作成しました（${data.status}）。Stripe から利用者へ請求メールが送られます。`
      );
      void queryClient.invalidateQueries({ queryKey: ['billing', 'admin-subscriptions'] });
    },
    onError: (err: unknown) => {
      setNotice('');
      setError(billingApiMessage(err, '請求を開始できませんでした'));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (stripeSubscriptionId: string) =>
      billingService.cancelAdminSubscription(stripeSubscriptionId),
    onSuccess: () => {
      setError('');
      setNotice('期間末で解約する予約をしました。');
      void queryClient.invalidateQueries({ queryKey: ['billing', 'admin-subscriptions'] });
    },
    onError: (err: unknown) => {
      setNotice('');
      setError(billingApiMessage(err, '解約予約ができませんでした'));
    },
  });

  const rows = rosterQuery.data ?? [];

  return (
    <div className="mt-3">
      <Alert variant="info">
        利用者の画面には申し込みを出しません。ここから請求すると、Stripe
        が利用者のメールへ請求を送ります。ログイン後の機能を未払いで止める門（BILLING_ENABLED）は、ここではオンにしません。
      </Alert>
      {configQuery.data && !configQuery.data.stripeConfigured ? (
        <Alert variant="warning">
          本番または開発の環境変数に STRIPE_SECRET_KEY
          が無いため、請求の開始はまだできません。一覧は確認できます。
        </Alert>
      ) : null}
      {notice ? <Alert variant="success">{notice}</Alert> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}
      <Card className="mb-3">
        <Card.Body>
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              setNotice('');
              setError('');
              createMutation.mutate();
            }}
          >
            <Row className="g-3 align-items-end">
              <Col md={5}>
                <Form.Label>利用者</Form.Label>
                <Form.Select
                  value={userId}
                  onChange={(event) => setUserId(event.target.value)}
                  required
                >
                  <option value="">選択</option>
                  {rows.map((row) => (
                    <option key={row.userId} value={row.userId}>
                      {row.name || row.username}（{row.email}）
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Stripe 価格ID</Form.Label>
                <Form.Control
                  value={priceId}
                  onChange={(event) => setPriceId(event.target.value)}
                  placeholder="price_..."
                  required
                />
              </Col>
              <Col md={3}>
                <Button type="submit" disabled={createMutation.isPending || !userId}>
                  {createMutation.isPending ? '作成中…' : '請求を開始'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      {rosterQuery.isLoading ? (
        <Spinner animation="border" role="status">
          <span className="visually-hidden">読み込み中</span>
        </Spinner>
      ) : (
        <Table responsive bordered hover size="sm">
          <thead>
            <tr>
              <th>利用者</th>
              <th>状態</th>
              <th>価格</th>
              <th>期間末</th>
              <th>免除</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const sub = row.subscription;
              const stripeSubscriptionId = sub?.stripeSubscriptionId ?? '';
              const canCancel =
                stripeSubscriptionId.length > 0 &&
                (sub?.status === 'active' || sub?.status === 'trialing') &&
                !sub?.cancelAtPeriodEnd;
              return (
                <tr key={row.userId}>
                  <td>
                    {row.name || row.username}
                    <div className="text-muted small">{row.email}</div>
                  </td>
                  <td>
                    {sub ? (
                      <Badge bg={sub.status === 'active' ? 'success' : 'secondary'}>
                        {sub.status}
                        {sub.cancelAtPeriodEnd ? ' / 期末解約' : ''}
                      </Badge>
                    ) : (
                      'なし'
                    )}
                  </td>
                  <td className="small">{sub?.plan || '—'}</td>
                  <td>{sub?.currentPeriodEnd ? sub.currentPeriodEnd.slice(0, 10) : '—'}</td>
                  <td>{overrideLabel(row)}</td>
                  <td>
                    {canCancel ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={cancelMutation.isPending}
                        onClick={() => cancelMutation.mutate(stripeSubscriptionId)}
                      >
                        期末で解約
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default function BillingManager() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const queryClient = useQueryClient();
  const [selectedInvoice, setSelectedInvoice] = useState<BillingInvoice | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<BillingDocument | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices');
  const [settings, setSettings] = useState<BillingSettings>({});
  const [templateSettings, setTemplateSettings] = useState({
    lang: 'ja',
    type: 'reminder',
    subject: '',
    body: '',
  });
  const [documentError, setDocumentError] = useState('');
  const [documentForm, setDocumentForm] = useState({
    type: 'quote' as 'quote' | 'delivery' | 'invoice',
    title: '',
    notes: '',
    dueAt: '',
  });
  const [documentItems, setDocumentItems] = useState([
    { description: '', quantity: 1, unitPriceUsd: 0, taxUsd: 0 },
  ]);
  const [organizationConfirmed, setOrganizationConfirmed] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [isExportingIssueHistory, setIsExportingIssueHistory] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    type: '',
    status: '',
    documentNumber: '',
    recipientQuery: '',
    actorUserId: '',
    issuedFrom: '',
    issuedTo: '',
    sortBy: 'issuedAt',
    sortOrder: 'desc',
  });

  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useQuery({
    queryKey: ['billing', 'subscriptions'],
    queryFn: () => billingService.getSubscriptions(),
  });

  // ダッシュボード統計（管理者のみ）
  const { data: dashboardStats, isLoading: isLoadingDashboard } = useQuery<DashboardStats>({
    queryKey: ['billing', 'dashboard-stats'],
    queryFn: async () => {
      try {
        const response = await api.get<{ ok: boolean; data: DashboardStats }>(
          '/billing/admin/dashboard-stats'
        );
        return response.data.data;
      } catch (error: any) {
        // APIが存在しない場合は空のデータを返す
        if (error.response?.status === 404) {
          return {
            mrr_usd: 0,
            current_month_revenue_usd: 0,
            last_month_revenue_usd: 0,
            last_year_month_revenue_usd: 0,
            yoy_growth_percent: 0,
            active_users: 0,
            arpu_usd: 0,
            new_subscriptions: 0,
            cancelled_subscriptions: 0,
            monthly_revenue: [],
          };
        }
        throw error;
      }
    },
    enabled: isAdmin && activeTab === 'dashboard',
  });

  // 設定取得（管理者のみ）
  const { data: fetchedSettings, isLoading: isLoadingSettings } = useQuery<BillingSettings>({
    queryKey: ['billing', 'settings'],
    queryFn: async () => {
      try {
        const response = await api.get<{ data: BillingSettings }>('/billing/admin/settings');
        return response.data.data;
      } catch (error: any) {
        // APIが存在しない場合は空のオブジェクトを返す
        if (error.response?.status === 404) {
          return {};
        }
        throw error;
      }
    },
    enabled: isAdmin && activeTab === 'settings',
  });

  useEffect(() => {
    if (fetchedSettings) setSettings(fetchedSettings);
  }, [fetchedSettings]);

  const { data: historyUsers } = useQuery({
    queryKey: ['billing', 'admin-users'],
    queryFn: () => billingService.getUsers(),
    enabled: isAdmin && activeTab === 'document-history',
  });

  const { data: documentIssueHistory, isLoading: isLoadingIssueHistory } = useQuery({
    queryKey: ['billing', 'document-issue-history', historyPage, historyFilters],
    queryFn: () =>
      billingService.getDocumentIssueHistory({
        page: historyPage,
        limit: 50,
        type: historyFilters.type || undefined,
        status: historyFilters.status || undefined,
        documentNumber: historyFilters.documentNumber || undefined,
        recipientQuery: historyFilters.recipientQuery || undefined,
        actorUserId: historyFilters.actorUserId ? Number(historyFilters.actorUserId) : undefined,
        issuedFrom: historyFilters.issuedFrom || undefined,
        issuedTo: historyFilters.issuedTo || undefined,
        sortBy: historyFilters.sortBy || undefined,
        sortOrder: historyFilters.sortOrder as 'asc' | 'desc',
      }),
    enabled: isAdmin && activeTab === 'document-history',
  });

  // 設定保存
  const saveSettingsMutation = useMutation({
    mutationFn: async (data: BillingSettings) => {
      try {
        const response = await api.post<{ ok: boolean }>('/billing/admin/settings', data);
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error(
            t('billing.settings_save_failed', { defaultValue: '設定APIが利用できません' })
          );
        }
        throw error;
      }
    },
    onSuccess: () => {
      alert(t('billing.settings_saved', { defaultValue: '保存しました' }));
    },
    onError: (error: any) => {
      const errorMessage =
        error.message || t('billing.settings_save_failed', { defaultValue: '保存に失敗しました' });
      const displayMessage = errorMessage.startsWith('api.error.')
        ? t(errorMessage, { defaultValue: errorMessage })
        : errorMessage;
      alert(displayMessage);
    },
  });

  // テンプレート設定読み込み
  useEffect(() => {
    if (fetchedSettings && activeTab === 'settings') {
      const subjectKey = `MAIL_TEMPLATE_${templateSettings.type.toUpperCase()}_SUBJECT_${templateSettings.lang.toUpperCase()}`;
      const bodyKey = `MAIL_TEMPLATE_${templateSettings.type.toUpperCase()}_BODY_${templateSettings.lang.toUpperCase()}`;
      setTemplateSettings({
        ...templateSettings,
        subject: fetchedSettings[subjectKey] || '',
        body: fetchedSettings[bodyKey] || '',
      });
    }
  }, [fetchedSettings, templateSettings.type, templateSettings.lang, activeTab]);

  const handleInvoiceClick = (invoice: BillingInvoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  const handleDocumentClick = (document: BillingDocument) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate(settings);
  };

  const handleSaveTemplate = () => {
    const subjectKey = `MAIL_TEMPLATE_${templateSettings.type.toUpperCase()}_SUBJECT_${templateSettings.lang.toUpperCase()}`;
    const bodyKey = `MAIL_TEMPLATE_${templateSettings.type.toUpperCase()}_BODY_${templateSettings.lang.toUpperCase()}`;
    const templateData = {
      [subjectKey]: templateSettings.subject,
      [bodyKey]: templateSettings.body,
    };
    saveSettingsMutation.mutate(templateData);
  };

  const formatIssueDate = (value?: string | null) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString(i18n.language);
    } catch (error) {
      return value;
    }
  };

  const formatUserDisplay = (user?: {
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
    email?: string | null;
    organizationName?: string | null;
  }) => {
    if (!user) return '-';
    const fullName = [user.lastName, user.firstName].filter(Boolean).join(' ');
    return (
      fullName ||
      user.organizationName ||
      user.username ||
      user.email ||
      t('common.unknown', { defaultValue: '不明' })
    );
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'issued':
        return 'success';
      case 'void':
        return 'secondary';
      default:
        return 'light';
    }
  };

  const handleExportIssueHistoryCsv = async () => {
    setIsExportingIssueHistory(true);
    try {
      const blob = await billingService.downloadDocumentIssueHistoryCsv({
        type: historyFilters.type || undefined,
        status: historyFilters.status || undefined,
        documentNumber: historyFilters.documentNumber || undefined,
        recipientQuery: historyFilters.recipientQuery || undefined,
        actorUserId: historyFilters.actorUserId ? Number(historyFilters.actorUserId) : undefined,
        issuedFrom: historyFilters.issuedFrom || undefined,
        issuedTo: historyFilters.issuedTo || undefined,
        sortBy: historyFilters.sortBy || undefined,
        sortOrder: historyFilters.sortOrder as 'asc' | 'desc',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `document-issue-history_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Issue history export error:', error);
      alert(t('common.export_failed', { defaultValue: 'エクスポートに失敗しました' }));
    } finally {
      setIsExportingIssueHistory(false);
    }
  };

  const handleHistoryPdfDownload = async (billingDoc: BillingDocument) => {
    try {
      const blob = await billingService.downloadDocumentPdf(billingDoc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document_${billingDoc.documentNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
      alert(t('billing.invoice_download_failed'));
    }
  };

  const createDocumentMutation = useMutation({
    mutationFn: async () => {
      setDocumentError('');
      return billingService.createDocument({
        type: documentForm.type,
        title: documentForm.title || undefined,
        notes: documentForm.notes || undefined,
        dueAt: documentForm.dueAt ? documentForm.dueAt : null,
        items: documentItems.map((item) => ({
          description: item.description,
          quantity: Number(item.quantity || 1),
          unitPriceCentsUsd: Math.round(Number(item.unitPriceUsd || 0) * 100),
          taxCentsUsd: Math.round(Number(item.taxUsd || 0) * 100),
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
      setDocumentForm({ ...documentForm, title: '', notes: '', dueAt: '' });
      setDocumentItems([{ description: '', quantity: 1, unitPriceUsd: 0, taxUsd: 0 }]);
      setOrganizationConfirmed(false);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message;
      if (message && typeof message === 'string' && message.startsWith('billing.')) {
        setDocumentError(t(message, { defaultValue: message }));
      } else {
        setDocumentError(message || t('common.error', { defaultValue: 'エラーが発生しました' }));
      }
    },
  });

  const handleCreateDocument = () => {
    setDocumentError('');
    if (!user?.organizationName) {
      setDocumentError(
        t('billing.organization_info_required', {
          defaultValue: '組織情報の登録が必要です。プロフィール画面で更新してください。',
        })
      );
      return;
    }
    if (!organizationConfirmed) {
      setDocumentError(
        t('billing.organization_info_confirm_required', {
          defaultValue: '組織情報が正しいことを確認してください。',
        })
      );
      return;
    }
    createDocumentMutation.mutate();
  };

  const submitApprovalMutation = useMutation({
    mutationFn: (document: BillingDocument) =>
      billingService.submitDocumentForApproval(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const approveDocumentMutation = useMutation({
    mutationFn: (document: BillingDocument) => billingService.approveDocument(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const rejectDocumentMutation = useMutation({
    mutationFn: (payload: { document: BillingDocument; reason: string }) =>
      billingService.rejectDocument(payload.document.id, payload.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const issueDocumentMutation = useMutation({
    mutationFn: (document: BillingDocument) => billingService.issueDocument(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const convertDocumentMutation = useMutation({
    mutationFn: (document: BillingDocument) => billingService.convertDocument(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const resendDocumentMutation = useMutation({
    mutationFn: (document: BillingDocument) => billingService.resendDocument(document.id),
  });

  const requestReissueMutation = useMutation({
    mutationFn: (payload: { document: BillingDocument; reason?: string }) =>
      billingService.requestDocumentReissue(payload.document.id, {
        reason: payload.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const approveReissueMutation = useMutation({
    mutationFn: (document: BillingDocument) => billingService.approveDocumentReissue(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const rejectReissueMutation = useMutation({
    mutationFn: (payload: { document: BillingDocument; reason: string }) =>
      billingService.rejectDocumentReissue(payload.document.id, payload.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const voidDocumentMutation = useMutation({
    mutationFn: (document: BillingDocument) => billingService.voidDocument(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'documents'] });
    },
  });

  const handleAddDocumentItem = () => {
    setDocumentItems([
      ...documentItems,
      { description: '', quantity: 1, unitPriceUsd: 0, taxUsd: 0 },
    ]);
  };

  const handleRemoveDocumentItem = (index: number) => {
    setDocumentItems(documentItems.filter((_, idx) => idx !== index));
  };

  const handleTemplateTypeChange = (type: string) => {
    setTemplateSettings({ ...templateSettings, type });
  };

  const handleTemplateLangChange = (lang: string) => {
    setTemplateSettings({ ...templateSettings, lang });
  };

  // チャートデータ
  const chartData = dashboardStats
    ? {
        labels: dashboardStats.monthly_revenue.map((r) => r.month),
        datasets: [
          {
            label: t('billing.dashboard_monthly_revenue', { defaultValue: '月次売上推移' }),
            data: dashboardStats.monthly_revenue.map((r) => r.revenue_usd),
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.1,
          },
        ],
      }
    : null;

  return (
    <Container className="mt-4">
      {!isBillingEnabled() && (
        <Alert variant="success" className="mb-4">
          <Alert.Heading>
            <strong>
              ✨{' '}
              {t('billing.free_period_notice', {
                defaultValue: '現在は開発期間中に付き無料でご利用になれます',
              })}
            </strong>
          </Alert.Heading>
          <p className="mb-0">
            {t('billing.free_period_description', {
              defaultValue:
                '料金徴収機能は準備済みですが、現在はすべての機能を無料でご利用いただけます。',
            })}
          </p>
        </Alert>
      )}
      <h1>{t('billing.title', { defaultValue: '請求管理' })}</h1>

      {/* 使い方の説明 */}
      <CollapsibleHelpSection
        titleKey="billing.how_to_use_title"
        descriptionKey="billing.how_to_use_desc"
        stepsKey={[
          'billing.how_to_use_step1',
          'billing.how_to_use_step2',
          'billing.how_to_use_step3',
        ]}
        storageKey="billing_manager"
      />

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || 'invoices')}
        className="mb-4 mt-4"
      >
        {/* ダッシュボードタブ（管理者のみ） */}
        {isAdmin && (
          <Tab
            eventKey="dashboard"
            title={t('billing.dashboard_title', { defaultValue: 'ダッシュボード' })}
          >
            <div className="mt-3">
              {isLoadingDashboard ? (
                <div className="text-center mt-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </Spinner>
                </div>
              ) : dashboardStats ? (
                <>
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <h5 className="card-title">
                            {t('billing.dashboard_mrr', { defaultValue: 'MRR' })}
                          </h5>
                          <h3 className="mb-0">${dashboardStats.mrr_usd.toFixed(2)}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <h5 className="card-title">
                            {t('billing.dashboard_current_month_revenue', {
                              defaultValue: '今月の売上',
                            })}
                          </h5>
                          <h3 className="mb-0">
                            ${dashboardStats.current_month_revenue_usd.toFixed(2)}
                          </h3>
                          <small className="text-muted">
                            {t('billing.dashboard_yoy_growth', { defaultValue: '前年同月比' })}:{' '}
                            {dashboardStats.yoy_growth_percent >= 0 ? '+' : ''}
                            {dashboardStats.yoy_growth_percent.toFixed(2)}%
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <h5 className="card-title">
                            {t('billing.dashboard_active_users', {
                              defaultValue: 'アクティブユーザー',
                            })}
                          </h5>
                          <h3 className="mb-0">{dashboardStats.active_users}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <h5 className="card-title">
                            {t('billing.dashboard_arpu', { defaultValue: 'ARPU' })}
                          </h5>
                          <h3 className="mb-0">${dashboardStats.arpu_usd.toFixed(2)}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col md={6}>
                      <Card>
                        <Card.Body>
                          <h5 className="card-title">
                            {t('billing.dashboard_monthly_revenue', {
                              defaultValue: '月次売上推移',
                            })}
                          </h5>
                          {chartData ? (
                            <Line
                              data={chartData}
                              options={{ responsive: true, scales: { y: { beginAtZero: true } } }}
                            />
                          ) : (
                            <p className="text-muted">
                              {t('common.no_data', { defaultValue: 'データがありません' })}
                            </p>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card>
                        <Card.Body>
                          <h5 className="card-title">
                            {t('billing.dashboard_subscription_stats', {
                              defaultValue: 'サブスクリプション統計',
                            })}
                          </h5>
                          <p>
                            {t('billing.dashboard_new_subscriptions', {
                              defaultValue: '新規登録（今月）',
                            })}
                            : <strong>{dashboardStats.new_subscriptions}</strong>
                          </p>
                          <p>
                            {t('billing.dashboard_cancelled_subscriptions', {
                              defaultValue: '解約（今月）',
                            })}
                            : <strong>{dashboardStats.cancelled_subscriptions}</strong>
                          </p>
                          <div className="mt-3">
                            <Button
                              variant="primary"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                // TODO: CSVエクスポート機能は未実装
                                // window.open('/api/billing/admin/export-csv?type=monthly', '_blank');
                                alert(
                                  t('billing.feature_not_implemented', {
                                    defaultValue: 'この機能は未実装です',
                                  })
                                );
                              }}
                            >
                              {t('billing.dashboard_export_monthly', {
                                defaultValue: '月次レポートCSV',
                              })}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                // TODO: CSVエクスポート機能は未実装
                                // window.open('/api/billing/admin/export-csv?type=transactions', '_blank');
                                alert(
                                  t('billing.feature_not_implemented', {
                                    defaultValue: 'この機能は未実装です',
                                  })
                                );
                              }}
                            >
                              {t('billing.dashboard_export_transactions', {
                                defaultValue: '取引明細CSV',
                              })}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                // TODO: CSVエクスポート機能は未実装
                                // window.open('/api/billing/admin/export-csv?type=annual', '_blank');
                                alert(
                                  t('billing.feature_not_implemented', {
                                    defaultValue: 'この機能は未実装です',
                                  })
                                );
                              }}
                            >
                              {t('billing.dashboard_export_annual', {
                                defaultValue: '年次レポートCSV',
                              })}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              ) : (
                <Alert variant="warning">
                  {t('billing.admin_only', { defaultValue: 'この機能は管理者のみ利用できます' })}
                </Alert>
              )}
            </div>
          </Tab>
        )}

        {/* 管理者向けサブスクリプション管理タブ */}
        {isAdmin && (
          <Tab
            eventKey="admin-subscriptions"
            title={t('billing.admin_subscriptions', { defaultValue: 'サブスクリプション管理' })}
          >
            <AdminSubscriptionPanel />
          </Tab>
        )}

        {/* 管理者向け請求書管理タブ */}
        {isAdmin && (
          <Tab
            eventKey="admin-invoices"
            title={t('billing.admin_invoices', { defaultValue: '請求書管理' })}
          >
            <div className="mt-3">
              <Alert variant="info" className="mb-3">
                {t('billing.admin_invoices_desc', { defaultValue: '全ユーザーの請求書を管理' })}
              </Alert>
              <Alert variant="warning">
                {t('common.coming_soon', { defaultValue: 'この機能は開発中です' })}
              </Alert>
            </div>
          </Tab>
        )}

        {/* 管理者向けユーザー管理タブ */}
        {isAdmin && (
          <Tab
            eventKey="admin-users"
            title={t('billing.admin_users', { defaultValue: 'ユーザー管理' })}
          >
            <div className="mt-3">
              <Alert variant="info" className="mb-3">
                {t('billing.admin_users_desc', {
                  defaultValue: '請求管理に関連するユーザー情報を確認',
                })}
              </Alert>
              <Alert variant="warning">
                {t('common.coming_soon', { defaultValue: 'この機能は開発中です' })}
              </Alert>
            </div>
          </Tab>
        )}

        {/* 管理者向け書類発行履歴タブ */}
        {isAdmin && (
          <Tab
            eventKey="document-history"
            title={t('billing.document_issue_history', {
              defaultValue: '書類発行履歴',
            })}
          >
            <div className="mt-3">
              <Alert variant="info" className="mb-3">
                {t('billing.document_issue_history_desc', {
                  defaultValue: '発行済み書類の履歴を検索・確認できます。',
                })}
              </Alert>
              <Card className="mb-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={3}>
                      <Form.Label>{t('billing.document_issue_history_filter_type')}</Form.Label>
                      <Form.Select
                        data-testid="document-history-filter-type"
                        value={historyFilters.type}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, type: e.target.value });
                        }}
                      >
                        <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                        <option value="quote">{t('billing.document_type_quote')}</option>
                        <option value="delivery">{t('billing.document_type_delivery')}</option>
                        <option value="invoice">{t('billing.document_type_invoice')}</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label>{t('billing.document_issue_history_filter_status')}</Form.Label>
                      <Form.Select
                        data-testid="document-history-filter-status"
                        value={historyFilters.status}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, status: e.target.value });
                        }}
                      >
                        <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                        <option value="issued">
                          {t('billing.document_status_issued', { defaultValue: '発行済み' })}
                        </option>
                        <option value="void">
                          {t('billing.document_status_void', { defaultValue: '無効' })}
                        </option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label>
                        {t('billing.document_issue_history_filter_document_number')}
                      </Form.Label>
                      <Form.Control
                        data-testid="document-history-filter-document-number"
                        value={historyFilters.documentNumber}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, documentNumber: e.target.value });
                        }}
                        placeholder={t(
                          'billing.document_issue_history_filter_document_number_placeholder'
                        )}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>
                        {t('billing.document_issue_history_filter_recipient')}
                      </Form.Label>
                      <Form.Control
                        data-testid="document-history-filter-recipient"
                        value={historyFilters.recipientQuery}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, recipientQuery: e.target.value });
                        }}
                        placeholder={t(
                          'billing.document_issue_history_filter_recipient_placeholder'
                        )}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>{t('billing.document_issue_history_filter_issuer')}</Form.Label>
                      <Form.Select
                        data-testid="document-history-filter-issuer"
                        value={historyFilters.actorUserId}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, actorUserId: e.target.value });
                        }}
                      >
                        <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                        {historyUsers?.data?.map((historyUser: any) => (
                          <option key={`issuer-${historyUser.id}`} value={historyUser.id}>
                            {formatUserDisplay(historyUser)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label>
                        {t('billing.document_issue_history_filter_issued_from')}
                      </Form.Label>
                      <Form.Control
                        data-testid="document-history-filter-issued-from"
                        type="date"
                        value={historyFilters.issuedFrom}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, issuedFrom: e.target.value });
                        }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>
                        {t('billing.document_issue_history_filter_issued_to')}
                      </Form.Label>
                      <Form.Control
                        data-testid="document-history-filter-issued-to"
                        type="date"
                        value={historyFilters.issuedTo}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, issuedTo: e.target.value });
                        }}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>{t('billing.document_issue_history_sort_by')}</Form.Label>
                      <Form.Select
                        data-testid="document-history-sort-by"
                        value={historyFilters.sortBy}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, sortBy: e.target.value });
                        }}
                      >
                        <option value="issuedAt">
                          {t('billing.document_issue_history_sort_issued_at')}
                        </option>
                        <option value="documentNumber">
                          {t('billing.document_issue_history_sort_document_number')}
                        </option>
                        <option value="status">
                          {t('billing.document_issue_history_sort_status')}
                        </option>
                        <option value="type">
                          {t('billing.document_issue_history_sort_type')}
                        </option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label>{t('billing.document_issue_history_sort_order')}</Form.Label>
                      <Form.Select
                        data-testid="document-history-sort-order"
                        value={historyFilters.sortOrder}
                        onChange={(e) => {
                          setHistoryPage(1);
                          setHistoryFilters({ ...historyFilters, sortOrder: e.target.value });
                        }}
                      >
                        <option value="desc">
                          {t('billing.document_issue_history_sort_desc')}
                        </option>
                        <option value="asc">{t('billing.document_issue_history_sort_asc')}</option>
                      </Form.Select>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        variant="outline-secondary"
                        data-testid="document-history-clear"
                        onClick={() => {
                          setHistoryPage(1);
                          setHistoryFilters({
                            type: '',
                            status: '',
                            documentNumber: '',
                            recipientQuery: '',
                            actorUserId: '',
                            issuedFrom: '',
                            issuedTo: '',
                            sortBy: 'issuedAt',
                            sortOrder: 'desc',
                          });
                        }}
                      >
                        {t('billing.document_issue_history_clear')}
                      </Button>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        variant="outline-primary"
                        data-testid="document-history-export"
                        onClick={handleExportIssueHistoryCsv}
                        disabled={isExportingIssueHistory}
                      >
                        {isExportingIssueHistory
                          ? t('common.exporting', { defaultValue: 'エクスポート中...' })
                          : `${t('common.export', { defaultValue: 'エクスポート' })} CSV`}
                      </Button>
                    </Col>
                  </Row>
                  <div className="text-muted small mt-2">
                    {t('billing.document_issue_history_export_note', {
                      defaultValue:
                        'CSVには書類番号・種別・発行日・発行先・税額・合計・発行者・再発行情報が含まれます（フィルター条件が適用されます）。',
                    })}
                  </div>
                </Card.Body>
              </Card>
              {isLoadingIssueHistory ? (
                <div className="text-center mt-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  <Table striped bordered hover data-testid="document-history-table">
                    <thead>
                      <tr>
                        <th>{t('billing.document_issue_history_col_issued_at')}</th>
                        <th>{t('billing.document_issue_history_col_document_number')}</th>
                        <th>{t('billing.document_issue_history_col_type')}</th>
                        <th>{t('billing.document_issue_history_col_status')}</th>
                        <th>{t('billing.document_issue_history_col_recipient')}</th>
                        <th>{t('billing.document_issue_history_col_issuer')}</th>
                        <th>{t('billing.document_issue_history_col_action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentIssueHistory?.data?.length ? (
                        documentIssueHistory.data.map((item) => (
                          <tr key={`issue-history-${item.document.id}`}>
                            <td>{formatIssueDate(item.document.issuedAt)}</td>
                            <td>{item.document.documentNumber}</td>
                            <td>{t(`billing.document_type_${item.document.type}`)}</td>
                            <td>
                              <Badge bg={getStatusVariant(item.document.status)}>
                                {t(`billing.document_status_${item.document.status}`, {
                                  defaultValue: item.document.status,
                                })}
                              </Badge>
                            </td>
                            <td>{formatUserDisplay(item.document.user)}</td>
                            <td>{formatUserDisplay(item.issuedBy || undefined)}</td>
                            <td>
                              <div className="mb-2">
                                {item.issuedAction === 'document_reissued'
                                  ? t('billing.document_issue_history_action_reissued')
                                  : t('billing.document_issue_history_action_issued')}
                              </div>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => handleHistoryPdfDownload(item.document)}
                              >
                                {t('common.download', { defaultValue: 'ダウンロード' })}{' '}
                                {t('common.pdf', { defaultValue: 'PDF' })}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            {t('billing.document_issue_history_no_results')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  {documentIssueHistory?.pagination && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        {t('billing.document_issue_history_pagination_total', {
                          total: documentIssueHistory.pagination.total,
                          start:
                            (documentIssueHistory.pagination.page - 1) *
                              documentIssueHistory.pagination.limit +
                            1,
                          end: Math.min(
                            documentIssueHistory.pagination.page *
                              documentIssueHistory.pagination.limit,
                            documentIssueHistory.pagination.total
                          ),
                        })}
                      </div>
                      <div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={documentIssueHistory.pagination.page === 1}
                          onClick={() => setHistoryPage(historyPage - 1)}
                        >
                          {t('billing.document_issue_history_pagination_prev')}
                        </Button>
                        <span className="mx-2">
                          {t('billing.document_issue_history_pagination_page', {
                            page: documentIssueHistory.pagination.page,
                            totalPages: documentIssueHistory.pagination.totalPages,
                          })}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={
                            documentIssueHistory.pagination.page ===
                            documentIssueHistory.pagination.totalPages
                          }
                          onClick={() => setHistoryPage(historyPage + 1)}
                        >
                          {t('billing.document_issue_history_pagination_next')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Tab>
        )}

        {/* 請求書タブ（ユーザー向け） */}
        <Tab eventKey="invoices" title={t('billing.invoices_list', { defaultValue: '請求書一覧' })}>
          <div className="mt-3">
            <InvoiceTable onInvoiceClick={handleInvoiceClick} />
          </div>
        </Tab>

        {/* 書類タブ（見積・納品・請求） */}
        <Tab eventKey="documents" title={t('billing.documents_list', { defaultValue: '書類一覧' })}>
          <div className="mt-3">
            <Card className="mb-4">
              <Card.Body>
                <h5>{t('billing.document_create', { defaultValue: '書類作成' })}</h5>
                <p className="text-muted small mb-3">
                  {t('billing.document_billing_info_required_note', {
                    defaultValue:
                      '書類作成には請求先情報の登録が必要です。プロフィール画面で登録してください。',
                  })}
                </p>
                <p className="text-muted small mb-3">
                  {t('billing.organization_info_note', {
                    defaultValue: '書類発行前に組織情報の確認が必要です。',
                  })}{' '}
                  <Link to="/profile">
                    {t('billing.update_profile_link', { defaultValue: 'プロフィールを更新' })}
                  </Link>
                </p>
                <Alert variant="info" className="mb-3">
                  {t('billing.document_language_policy_note', {
                    defaultValue: '発行される書類PDFは日本語のみです。',
                  })}
                </Alert>
                <Alert variant={user?.organizationName ? 'info' : 'warning'} className="mb-3">
                  <strong>
                    {t('billing.organization_info_label', { defaultValue: '組織情報' })}:
                  </strong>{' '}
                  {user?.organizationName
                    ? `${user.organizationName}${user.departmentName ? ` / ${user.departmentName}` : ''}`
                    : t('billing.organization_info_missing', { defaultValue: '未登録' })}
                </Alert>
                {!user?.organizationName && (
                  <div className="mb-3">
                    <Button as={Link as any} to="/profile" variant="outline-primary" size="sm">
                      {t('billing.update_profile_link', { defaultValue: 'プロフィールを更新' })}
                    </Button>
                  </div>
                )}
                {documentError && (
                  <Alert variant="danger" className="mb-3">
                    {documentError}
                  </Alert>
                )}
                <Form className="mt-3">
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          {t('billing.document_type', { defaultValue: '種別' })}
                        </Form.Label>
                        <Form.Select
                          value={documentForm.type}
                          onChange={(e) =>
                            setDocumentForm({
                              ...documentForm,
                              type: e.target.value as 'quote' | 'delivery' | 'invoice',
                            })
                          }
                        >
                          <option value="quote">
                            {t('billing.document_type_quote', { defaultValue: '見積書' })}
                          </option>
                          <option value="delivery">
                            {t('billing.document_type_delivery', { defaultValue: '納品書' })}
                          </option>
                          <option value="invoice">
                            {t('billing.document_type_invoice', { defaultValue: '請求書' })}
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>
                          {t('billing.document_title', { defaultValue: 'タイトル' })}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={documentForm.title}
                          onChange={(e) =>
                            setDocumentForm({ ...documentForm, title: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t('billing.document_due_at', { defaultValue: '支払期限' })}
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={documentForm.dueAt}
                          onChange={(e) =>
                            setDocumentForm({ ...documentForm, dueAt: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t('billing.document_notes', { defaultValue: '備考' })}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={documentForm.notes}
                          onChange={(e) =>
                            setDocumentForm({ ...documentForm, notes: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <h6>{t('billing.invoice_items', { defaultValue: '明細' })}</h6>
                    {documentItems.map((item, index) => (
                      <Row className="g-2 mb-2" key={`doc-item-${index}`}>
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder={t('billing.invoice_item_description', {
                              defaultValue: '品目',
                            })}
                            value={item.description}
                            onChange={(e) => {
                              const next = [...documentItems];
                              next[index] = { ...next[index], description: e.target.value };
                              setDocumentItems(next);
                            }}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={(e) => {
                              const next = [...documentItems];
                              next[index] = {
                                ...next[index],
                                quantity: Number(e.target.value || 1),
                              };
                              setDocumentItems(next);
                            }}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.unitPriceUsd}
                            onChange={(e) => {
                              const next = [...documentItems];
                              next[index] = {
                                ...next[index],
                                unitPriceUsd: Number(e.target.value || 0),
                              };
                              setDocumentItems(next);
                            }}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.taxUsd}
                            onChange={(e) => {
                              const next = [...documentItems];
                              next[index] = { ...next[index], taxUsd: Number(e.target.value || 0) };
                              setDocumentItems(next);
                            }}
                          />
                        </Col>
                        <Col md={1} className="d-flex align-items-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveDocumentItem(index)}
                            disabled={documentItems.length === 1}
                          >
                            {t('common.remove', { defaultValue: '削除' })}
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={handleAddDocumentItem}>
                      {t('common.add', { defaultValue: '追加' })}
                    </Button>
                  </div>

                  <Button
                    variant="primary"
                    onClick={handleCreateDocument}
                    disabled={createDocumentMutation.isPending}
                  >
                    {createDocumentMutation.isPending
                      ? t('common.saving', { defaultValue: '保存中...' })
                      : t('common.create', { defaultValue: '作成' })}
                  </Button>
                  <Form.Check
                    className="mt-3"
                    type="checkbox"
                    id="organization-confirm"
                    checked={organizationConfirmed}
                    onChange={(e) => setOrganizationConfirmed(e.target.checked)}
                    label={t('billing.organization_info_confirm_label', {
                      defaultValue: '組織情報が正しいことを確認しました',
                    })}
                  />
                </Form>
              </Card.Body>
            </Card>

            <InvoiceTable
              mode="documents"
              onDocumentClick={handleDocumentClick}
              isAdmin={isAdmin}
            />
          </div>
        </Tab>

        {/* サブスクリプションタブ（ユーザー向け） */}
        <Tab
          eventKey="subscriptions"
          title={t('billing.subscriptions', { defaultValue: 'サブスクリプション' })}
        >
          <div className="mt-3">
            {isLoadingSubscriptions ? (
              <div className="text-center mt-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </Spinner>
              </div>
            ) : subscriptions && subscriptions.length > 0 ? (
              <Row>
                {subscriptions.map((subscription) => (
                  <Col md={6} key={subscription.id} className="mb-3">
                    <SubscriptionCard subscription={subscription} />
                  </Col>
                ))}
              </Row>
            ) : (
              <Alert variant="info" className="mt-4">
                {t('billing.no_subscriptions', { defaultValue: 'サブスクリプションがありません' })}
              </Alert>
            )}
          </div>
        </Tab>

        {/* 設定タブ（管理者のみ） */}
        {isAdmin && (
          <Tab eventKey="settings" title={t('billing.settings_title', { defaultValue: '設定' })}>
            <div className="mt-3">
              {isLoadingSettings ? (
                <div className="text-center mt-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </Spinner>
                </div>
              ) : (
                <Tabs defaultActiveKey="basic" className="mb-4">
                  {/* 基本設定 */}
                  <Tab
                    eventKey="basic"
                    title={t('billing.settings_basic', { defaultValue: '基本設定' })}
                  >
                    <Card className="mt-3">
                      <Card.Body>
                        <Form>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_company_name', { defaultValue: '会社名' })}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={settings.INV_COMPANY_NAME || ''}
                              onChange={(e) =>
                                setSettings({ ...settings, INV_COMPANY_NAME: e.target.value })
                              }
                              placeholder="Your Company Name"
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_company_address', { defaultValue: '会社住所' })}
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={settings.INV_COMPANY_ADDRESS || ''}
                              onChange={(e) =>
                                setSettings({ ...settings, INV_COMPANY_ADDRESS: e.target.value })
                              }
                              placeholder="Address"
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_reg_number', {
                                defaultValue: '適格請求書登録番号',
                              })}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={settings.INV_REG_NUMBER || ''}
                              onChange={(e) =>
                                setSettings({ ...settings, INV_REG_NUMBER: e.target.value })
                              }
                              placeholder="T-XXXX-XXXX-XXXX"
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_mail_from', {
                                defaultValue: '送信元メール（From）',
                              })}
                            </Form.Label>
                            <Form.Control
                              type="email"
                              value={settings.MAIL_FROM || ''}
                              onChange={(e) =>
                                setSettings({ ...settings, MAIL_FROM: e.target.value })
                              }
                              placeholder="no-reply@example.com"
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_mail_admin', {
                                defaultValue: '管理者通知先メール',
                              })}
                            </Form.Label>
                            <Form.Control
                              type="email"
                              value={settings.MAIL_ADMIN || ''}
                              onChange={(e) =>
                                setSettings({ ...settings, MAIL_ADMIN: e.target.value })
                              }
                              placeholder="admin@example.com"
                            />
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              onClick={handleSaveSettings}
                              disabled={saveSettingsMutation.isPending}
                            >
                              {saveSettingsMutation.isPending ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  {t('common.saving', { defaultValue: '保存中...' })}
                                </>
                              ) : (
                                t('billing.settings_save', { defaultValue: '保存' })
                              )}
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Tab>

                  {/* メールテンプレート */}
                  <Tab
                    eventKey="templates"
                    title={t('billing.settings_templates', { defaultValue: 'メールテンプレート' })}
                  >
                    <Card className="mt-3">
                      <Card.Body>
                        <Form>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_template_lang', { defaultValue: '言語' })}
                            </Form.Label>
                            <Form.Select
                              value={templateSettings.lang}
                              onChange={(e) => handleTemplateLangChange(e.target.value)}
                            >
                              <option value="ja">
                                {t('common.language_ja', { defaultValue: '日本語' })}
                              </option>
                              <option value="en">
                                {t('common.language_en', { defaultValue: 'English' })}
                              </option>
                            </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_template_type', {
                                defaultValue: 'テンプレート種別',
                              })}
                            </Form.Label>
                            <Form.Select
                              value={templateSettings.type}
                              onChange={(e) => handleTemplateTypeChange(e.target.value)}
                            >
                              <option value="reminder">
                                {t('billing.settings_template_reminder', {
                                  defaultValue: '請求リマインド',
                                })}
                              </option>
                              <option value="payment_succeeded">
                                {t('billing.settings_template_payment_succeeded', {
                                  defaultValue: '支払い成功',
                                })}
                              </option>
                              <option value="payment_failed">
                                {t('billing.settings_template_payment_failed', {
                                  defaultValue: '支払い失敗',
                                })}
                              </option>
                              <option value="suspended">
                                {t('billing.settings_template_suspended', {
                                  defaultValue: 'アカウント停止',
                                })}
                              </option>
                            </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_template_subject', { defaultValue: '件名' })}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={templateSettings.subject}
                              onChange={(e) =>
                                setTemplateSettings({
                                  ...templateSettings,
                                  subject: e.target.value,
                                })
                              }
                            />
                            <Form.Text className="text-muted">
                              {t('billing.settings_placeholder_days_before', {
                                defaultValue: 'プレースホルダー: :days_before, :invoice_number',
                              })}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('billing.settings_template_body', { defaultValue: '本文' })}
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={10}
                              value={templateSettings.body}
                              onChange={(e) =>
                                setTemplateSettings({ ...templateSettings, body: e.target.value })
                              }
                            />
                            <Form.Text className="text-muted">
                              {t('billing.settings_placeholder_body', {
                                defaultValue:
                                  'プレースホルダー: :days_before, :next_billing_at, :invoice_number, :amount_usd, :pdf_path, :reason',
                              })}
                            </Form.Text>
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              onClick={handleSaveTemplate}
                              disabled={saveSettingsMutation.isPending}
                            >
                              {saveSettingsMutation.isPending ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  {t('common.saving', { defaultValue: '保存中...' })}
                                </>
                              ) : (
                                t('billing.settings_save', { defaultValue: '保存' })
                              )}
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setTemplateSettings({ ...templateSettings, subject: '', body: '' });
                              }}
                            >
                              {t('billing.settings_reset', { defaultValue: 'リセット' })}
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Tab>
                </Tabs>
              )}
            </div>
          </Tab>
        )}
      </Tabs>

      {selectedInvoice && (
        <InvoiceDetailModal
          show={showInvoiceModal}
          onHide={handleCloseInvoiceModal}
          invoice={selectedInvoice}
        />
      )}
      {selectedDocument && (
        <InvoiceDetailModal
          show={showDocumentModal}
          onHide={handleCloseDocumentModal}
          mode="documents"
          billingDocument={selectedDocument}
          isAdmin={isAdmin}
          onSubmitApproval={(document) => submitApprovalMutation.mutate(document)}
          onApprove={(document) => approveDocumentMutation.mutate(document)}
          onReject={(document, reason) => rejectDocumentMutation.mutate({ document, reason })}
          onIssue={(document) => issueDocumentMutation.mutate(document)}
          onConvert={(document) => convertDocumentMutation.mutate(document)}
          onResend={(document) => resendDocumentMutation.mutate(document)}
          onReissueRequest={(document, reason) =>
            requestReissueMutation.mutate({ document, reason })
          }
          onReissueApprove={(document) => approveReissueMutation.mutate(document)}
          onReissueReject={(document, reason) => rejectReissueMutation.mutate({ document, reason })}
          onVoid={(document) => voidDocumentMutation.mutate(document)}
        />
      )}
    </Container>
  );
}
